import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  collection,
  doc,
  query,
  updateDoc,
  where,
} from 'firebase/firestore';
import { safeOnSnapshot } from '../../../utils/firestoreSubscriptions';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase-client';
import './mentoring.css';

const call = (name, data) => httpsCallable(functions, name)(data).then((result) => result.data);
const EMPTY_CHECKIN_RESPONSE = {
  progress: '',
  nextSteps: '',
  supportNeeded: '',
  attentionRequested: false,
  adminSharedNote: '',
};

function MentoringModalPortal({ children }) {
  return typeof document === 'undefined' ? children : createPortal(children, document.body);
}

function csv(value) {
  return String(value || '').split(',').map((entry) => entry.trim()).filter(Boolean);
}

function dateLabel(value) {
  const date = value?.toDate?.()
    || (typeof value === 'number' || typeof value === 'string' ? new Date(value) : null);
  return date instanceof Date ? date.toLocaleDateString('en-GB', { dateStyle: 'medium' }) : 'Not scheduled';
}

function roleLabel(role) {
  return role === 'mentor' ? 'Mentor' : 'Mentee';
}

function statusLabel(status) {
  return String(status || 'not applied').replaceAll('_', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

const GUIDED_MEETING_FALLBACK = {
  kickoff: {
    title: 'Kick-off and mentoring agreement',
    agenda: [
      'Agree what would make the relationship worthwhile.',
      'Agree how, where, and how often you will meet.',
      'Discuss confidentiality, boundaries, and raising concerns.',
      'Choose one useful action before the next meeting.',
    ],
    progressLabel: 'What did you agree about purpose, expectations, and boundaries?',
  },
  goals: {
    title: 'Goals and first actions',
    agenda: [
      'Turn priorities into one or two shared goals.',
      'Describe what meaningful progress would look like.',
      'Choose realistic actions and support.',
      'Check that the meeting rhythm works for both people.',
    ],
    progressLabel: 'What goals and signs of progress did you agree?',
  },
  progress: {
    title: 'Progress and next steps',
    agenda: [
      'Review movement since the last conversation.',
      'Discuss learning, including anything that did not work.',
      'Identify the support or challenge needed now.',
      'Agree the next actions before finishing.',
    ],
    progressLabel: 'What has moved forward since the last conversation?',
  },
  reflection: {
    title: 'Programme reflection and next steps',
    agenda: [
      'Review progress against the shared goals.',
      'Name the most useful changes, learning, or achievements.',
      'Decide what should continue after the programme.',
      'Agree whether to close, extend, or reshape the relationship.',
    ],
    progressLabel: 'What changed or developed during the programme?',
  },
};

function guidedMeeting(checkIn = {}) {
  const fallback = GUIDED_MEETING_FALLBACK[checkIn.kind] || GUIDED_MEETING_FALLBACK.progress;
  return {
    ...fallback,
    title: checkIn.title || fallback.title,
    agenda: Array.isArray(checkIn.agenda) && checkIn.agenda.length ? checkIn.agenda : fallback.agenda,
  };
}

function Metric({ value, label }) {
  return (
    <div className="mentoring-metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function MentoringMemberPicker({
  label,
  members,
  value,
  onChange,
  placeholder,
  emptyMessage,
}) {
  const rootRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selectedMember = members.find((member) => member.id === value);
  const filteredMembers = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    return [...members]
      .filter((member) => !needle || String(member.displayName || 'Member').toLocaleLowerCase().includes(needle))
      .sort((left, right) => String(left.displayName || 'Member').localeCompare(String(right.displayName || 'Member')));
  }, [members, search]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOnClickAway = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', closeOnClickAway);
    return () => document.removeEventListener('mousedown', closeOnClickAway);
  }, [open]);

  const choose = (memberId) => {
    onChange(memberId);
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="mentoring-member-picker" ref={rootRef}>
      <span className="form-label">{label}</span>
      <button
        type="button"
        className={`mentoring-member-picker__control ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <span className={selectedMember ? 'mentoring-member-picker__value' : 'mentoring-member-picker__placeholder'}>
          {selectedMember?.displayName || placeholder}
        </span>
        <span className="mentoring-member-picker__chevron" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="mentoring-member-picker__menu">
          <input
            type="search"
            className="input mentoring-member-picker__search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={`Search ${label.toLocaleLowerCase()}s`}
            aria-label={`Search ${label.toLocaleLowerCase()}s`}
            autoFocus
          />
          <div className="mentoring-member-picker__options" role="listbox" aria-label={`${label} options`}>
            {filteredMembers.length === 0 && (
              <div className="mentoring-member-picker__empty">{emptyMessage}</div>
            )}
            {filteredMembers.map((member) => (
              <button
                key={member.id}
                type="button"
                className={`mentoring-member-picker__option ${member.id === value ? 'is-selected' : ''}`}
                onClick={() => choose(member.id)}
                role="option"
                aria-selected={member.id === value}
              >
                <strong>{member.displayName || 'Member'}</strong>
                {member.pickerNote && <span>{member.pickerNote}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ApplicationForm({ teamId, existing, invitation, onDone, onCancel }) {
  const [selectedRoles, setSelectedRoles] = useState(
    existing?.requestedRoles?.length
      ? existing.requestedRoles
      : existing?.adminAssignedRoles || existing?.approvedRoles || invitation?.invitedRoles || []
  );
  const [capacity, setCapacity] = useState(existing?.mentorCapacityRequested || 1);
  const [supportAreas, setSupportAreas] = useState((existing?.questionnaire?.supportAreas || []).join(', '));
  const [soughtAreas, setSoughtAreas] = useState((existing?.questionnaire?.soughtAreas || []).join(', '));
  const [interests, setInterests] = useState((existing?.questionnaire?.interests || []).join(', '));
  const [availability, setAvailability] = useState((existing?.questionnaire?.availability || []).join(', '));
  const [meetingFormats, setMeetingFormats] = useState(existing?.questionnaire?.meetingFormats || []);
  const [cadence, setCadence] = useState(existing?.questionnaire?.cadence || '');
  const [introduction, setIntroduction] = useState(existing?.questionnaire?.introduction || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggleRole = (role) => setSelectedRoles((current) => (
    current.includes(role) ? current.filter((entry) => entry !== role) : [...current, role]
  ));
  const toggleFormat = (format) => setMeetingFormats((current) => (
    current.includes(format) ? current.filter((entry) => entry !== format) : [...current, format]
  ));

  const submit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      await call('saveMentoringApplication', {
        teamId,
        roles: selectedRoles,
        mentorCapacityRequested: Number(capacity),
        questionnaire: {
          supportAreas: csv(supportAreas),
          soughtAreas: csv(soughtAreas),
          interests: csv(interests),
          availability: csv(availability),
          meetingFormats,
          cadence,
          introduction,
        },
      });
      onDone?.();
    } catch (err) {
      setError(err?.message || 'Could not save your mentoring application.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="modal-form mentoring-application-form" onSubmit={submit}>
      <fieldset className="mentoring-application-form__section">
        <legend>Your mentoring role</legend>
        <p className="text-muted">Choose one or both. An administrator will review the application before matching.</p>
        <div className="mentoring-role-options">
          <label className={selectedRoles.includes('mentor') ? 'is-selected' : ''}>
            <input type="checkbox" checked={selectedRoles.includes('mentor')} onChange={() => toggleRole('mentor')} />
            <span><strong>Offer to mentor</strong><small>Share experience and support another member’s development.</small></span>
          </label>
          <label className={selectedRoles.includes('mentee') ? 'is-selected' : ''}>
            <input type="checkbox" checked={selectedRoles.includes('mentee')} onChange={() => toggleRole('mentee')} />
            <span><strong>Request a mentor</strong><small>Get structured support with goals, confidence, skills, or development.</small></span>
          </label>
        </div>
      </fieldset>
      {selectedRoles.includes('mentor') && (
        <label className="form-group">
          <span className="form-label">Maximum mentees I can support</span>
          <input className="form-input" type="number" min="1" max="20" value={capacity} onChange={(event) => setCapacity(event.target.value)} />
        </label>
      )}
      <section className="mentoring-application-form__section">
        <h3>What would make a useful match?</h3>
        <p className="text-muted">These answers help explain matching suggestions to programme administrators.</p>
        <div className="mentoring-form__grid">
        <label className="form-group">
          <span className="form-label">Areas I can support</span>
          <input className="form-input" value={supportAreas} onChange={(event) => setSupportAreas(event.target.value)} placeholder="Leadership, confidence, skills" />
        </label>
        <label className="form-group">
          <span className="form-label">Areas where I want support</span>
          <input className="form-input" value={soughtAreas} onChange={(event) => setSoughtAreas(event.target.value)} placeholder="Leadership, confidence, skills" />
        </label>
        <label className="form-group">
          <span className="form-label">Interests</span>
          <input className="form-input" value={interests} onChange={(event) => setInterests(event.target.value)} placeholder="Comma-separated" />
        </label>
        <label className="form-group">
          <span className="form-label">Availability</span>
          <input className="form-input" value={availability} onChange={(event) => setAvailability(event.target.value)} placeholder="Weekday evenings, weekends" />
        </label>
        </div>
      </section>
      <fieldset className="mentoring-application-form__section">
        <legend>How would you like to meet?</legend>
        <div className="mentoring-format-options">
        {['In person', 'Video call', 'Phone', 'Messaging'].map((format) => (
          <label key={format}>
            <input type="checkbox" checked={meetingFormats.includes(format)} onChange={() => toggleFormat(format)} /> {format}
          </label>
        ))}
        </div>
      </fieldset>
      <label className="form-group">
        <span className="form-label">Preferred conversation rhythm</span>
        <select className="form-input" value={cadence} onChange={(event) => setCadence(event.target.value)}>
          <option value="">No preference</option>
          <option value="weekly">Weekly</option>
          <option value="fortnightly">Fortnightly</option>
          <option value="monthly">Monthly</option>
        </select>
      </label>
      <label className="form-group">
        <span className="form-label">Short introduction</span>
        <textarea className="form-textarea" value={introduction} onChange={(event) => setIntroduction(event.target.value)} maxLength={600} rows={4} placeholder="What would you like a potential mentoring partner to know?" />
      </label>
      {error && <p className="text-danger" role="alert">{error}</p>}
      <div className="form-actions mentoring-application-form__actions">
        <button className="btn btn--secondary" type="button" onClick={onCancel} disabled={busy}>Cancel</button>
        <button className="btn btn--primary" type="submit" disabled={busy || selectedRoles.length === 0}>
          {busy ? 'Saving…' : existing ? 'Update application' : 'Submit application'}
        </button>
      </div>
    </form>
  );
}

function MentoringApplicationModal({ teamId, existing, invitation, onDecline, onClose }) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <MentoringModalPortal>
    <div className="modal-backdrop mentoring-application-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal-content mentoring-application-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mentoring-application-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mentoring-modal__header">
          <div>
            <h2 id="mentoring-application-title">{existing ? 'Update mentoring application' : 'Apply for mentoring'}</h2>
            <p>Tell the programme team what you can offer, what support you want, and what would make a practical match.</p>
          </div>
          <button type="button" className="mentoring-modal__close" onClick={onClose} aria-label="Close mentoring application">×</button>
        </header>
        {invitation && ['sent', 'opened'].includes(invitation.status) && (
          <div className="mentoring-invitation-banner">
            <div>
              <strong>{invitation.sentByName || 'A programme administrator'} invited you to apply as {invitation.invitedRoles?.map(roleLabel).join(' or ').toLowerCase()}.</strong>
              {invitation.note && <p>{invitation.note}</p>}
              <small>Please respond by {dateLabel(invitation.deadlineAt)}. You can adjust the suggested role before submitting.</small>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={onDecline}>Decline invitation</button>
          </div>
        )}
        <ApplicationForm teamId={teamId} existing={existing} invitation={invitation} onDone={onClose} onCancel={onClose} />
      </div>
    </div>
    </MentoringModalPortal>
  );
}

function MentoringInviteModal({ teamId, members, currentUserId, onClose }) {
  const [memberId, setMemberId] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [note, setNote] = useState('');
  const [deadlineDays, setDeadlineDays] = useState(14);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const options = members.filter((member) => member.id !== currentUserId);
  const toggleRole = (role) => setSelectedRoles((current) => (
    current.includes(role) ? current.filter((entry) => entry !== role) : [...current, role]
  ));

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [busy, onClose]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await call('sendMentoringInvitation', {
        teamId,
        memberId,
        roles: selectedRoles,
        note,
        deadlineDays,
      });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Could not send the mentoring invitation.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <MentoringModalPortal>
    <div className="modal-backdrop mentoring-invite-backdrop" role="presentation" onMouseDown={() => !busy && onClose?.()}>
      <form
        className="modal-content mentoring-invite-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mentoring-invite-title"
        onSubmit={submit}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mentoring-modal__header">
          <div>
            <h2 id="mentoring-invite-title">Invite a member</h2>
            <p>Ask a current club member to complete a mentoring profile. They decide whether to participate.</p>
          </div>
          <button type="button" className="mentoring-modal__close" onClick={onClose} disabled={busy} aria-label="Close mentoring invitation">×</button>
        </header>
        <div className="mentoring-invite-modal__body">
          <MentoringMemberPicker
            label="Member"
            members={options}
            value={memberId}
            onChange={setMemberId}
            placeholder="Choose a club member"
            emptyMessage="No club members match that search."
          />
          <fieldset className="mentoring-application-form__section">
            <legend>Invite them to participate as</legend>
            <div className="mentoring-role-options">
              <label className={selectedRoles.includes('mentor') ? 'is-selected' : ''}>
                <input type="checkbox" checked={selectedRoles.includes('mentor')} onChange={() => toggleRole('mentor')} />
                <span><strong>Mentor</strong><small>Ask them to offer experience and support.</small></span>
              </label>
              <label className={selectedRoles.includes('mentee') ? 'is-selected' : ''}>
                <input type="checkbox" checked={selectedRoles.includes('mentee')} onChange={() => toggleRole('mentee')} />
                <span><strong>Mentee</strong><small>Ask them to describe the support they would value.</small></span>
              </label>
            </div>
          </fieldset>
          <label className="form-group">
            <span className="form-label">Personal message <small>(optional)</small></span>
            <textarea className="form-textarea" rows={4} maxLength={600} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Why do you think this programme could be useful for them?" />
          </label>
          <label className="form-group">
            <span className="form-label">Response deadline</span>
            <select className="form-input" value={deadlineDays} onChange={(event) => setDeadlineDays(Number(event.target.value))}>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </label>
          {error && <p className="text-danger" role="alert">{error}</p>}
        </div>
        <footer className="form-actions mentoring-invite-modal__actions">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy || !memberId || selectedRoles.length === 0}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </footer>
      </form>
    </div>
    </MentoringModalPortal>
  );
}

function ParticipantReviewModal({
  participant,
  capacity,
  onCapacityChange,
  onReview,
  busy,
  onClose,
}) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [busy, onClose]);

  if (!participant) return null;
  const questionnaire = participant.questionnaire || {};
  const rolesForApproval = participant.requestedRoles?.length
    ? participant.requestedRoles
    : participant.adminAssignedRoles || participant.approvedRoles || [];
  const answer = (value) => {
    if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not supplied';
    return value || 'Not supplied';
  };
  const act = async (status) => {
    const completed = await onReview({ ...participant, requestedRoles: rolesForApproval }, status);
    if (completed) onClose?.();
  };

  return (
    <MentoringModalPortal>
    <div className="modal-backdrop mentoring-review-backdrop" role="presentation" onMouseDown={() => !busy && onClose?.()}>
      <div
        className="modal-content mentoring-review-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mentoring-review-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mentoring-modal__header">
          <div>
            <h2 id="mentoring-review-title">{participant.displayName || 'Mentoring application'}</h2>
            <p>{statusLabel(participant.status)} · {rolesForApproval.map(roleLabel).join(' and ') || 'No role selected'}</p>
          </div>
          <button type="button" className="mentoring-modal__close" onClick={onClose} disabled={busy} aria-label="Close mentoring application review">×</button>
        </header>
        <div className="mentoring-review-modal__body">
          {participant.isUnder18 && (
            <div className={participant.parentConfirmed ? 'mentoring-review-notice is-confirmed' : 'mentoring-review-notice'}>
              <strong>Youth mentoring</strong>
              <span>Linked-parent confirmation {participant.parentConfirmed ? 'has been received.' : 'is still required before approval.'}</span>
            </div>
          )}
          <section>
            <h3>Application</h3>
            <dl className="mentoring-review-grid">
              <div><dt>Role requested</dt><dd>{rolesForApproval.map(roleLabel).join(' and ') || 'Not supplied'}</dd></div>
              {rolesForApproval.includes('mentor') && (
                <div>
                  <dt>Requested capacity</dt>
                  <dd>{participant.mentorCapacityRequested || 1} mentee{Number(participant.mentorCapacityRequested || 1) === 1 ? '' : 's'}</dd>
                </div>
              )}
              <div><dt>Can support</dt><dd>{answer(questionnaire.supportAreas)}</dd></div>
              <div><dt>Wants support with</dt><dd>{answer(questionnaire.soughtAreas)}</dd></div>
              <div><dt>Interests</dt><dd>{answer(questionnaire.interests)}</dd></div>
              <div><dt>Availability</dt><dd>{answer(questionnaire.availability)}</dd></div>
              <div><dt>Meeting formats</dt><dd>{answer(questionnaire.meetingFormats)}</dd></div>
              <div><dt>Preferred rhythm</dt><dd>{answer(questionnaire.cadence)}</dd></div>
            </dl>
          </section>
          <section>
            <h3>Introduction</h3>
            <p className={questionnaire.introduction ? '' : 'text-muted'}>{questionnaire.introduction || 'No introduction was supplied.'}</p>
          </section>
          {rolesForApproval.includes('mentor') && (
            <label className="form-group mentoring-review-capacity">
              <span className="form-label">Approved mentor capacity</span>
              <input
                className="form-input"
                type="number"
                min="1"
                max="20"
                value={capacity}
                onChange={(event) => onCapacityChange(event.target.value)}
              />
              <small>The effective capacity remains the lower of their requested capacity and this admin limit.</small>
            </label>
          )}
        </div>
        <footer className="form-actions mentoring-review-modal__actions">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="btn btn--ghost" onClick={() => act('paused')} disabled={busy}>Pause</button>
          <button type="button" className="btn btn--danger" onClick={() => act('rejected')} disabled={busy}>Reject</button>
          <button type="button" className="btn btn--primary" onClick={() => act('approved')} disabled={busy || (participant.isUnder18 && !participant.parentConfirmed)}>
            {busy ? 'Saving…' : participant.status === 'approved' ? 'Save approval' : 'Approve'}
          </button>
        </footer>
      </div>
    </div>
    </MentoringModalPortal>
  );
}

export function PairingGuidance({ teamId, pairing, currentUserId = '', isAdmin = false, canSubmit = true }) {
  const [goals, setGoals] = useState([]);
  const [checkIns, setCheckIns] = useState([]);
  const [responsesByCheckIn, setResponsesByCheckIn] = useState({});
  const [goalTitle, setGoalTitle] = useState('');
  const [response, setResponse] = useState({ ...EMPTY_CHECKIN_RESPONSE });
  const [editingCheckInId, setEditingCheckInId] = useState('');
  const [openedEarlyCheckInIds, setOpenedEarlyCheckInIds] = useState([]);
  const [expandedAdminCheckInIds, setExpandedAdminCheckInIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [guidanceLoadErrors, setGuidanceLoadErrors] = useState({ goals: '', checkIns: '' });

  useEffect(() => {
    if (!pairing?.id || pairing.programmeMode !== 'guided') return undefined;
    const stopGoals = safeOnSnapshot(collection(db, 'teams', teamId, 'mentoringPairings', pairing.id, 'goals'), (snap) => {
      setGoals(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      setGuidanceLoadErrors((current) => ({ ...current, goals: '' }));
    }, (loadError) => {
      console.error('Could not load shared mentoring goals.', loadError);
      setGuidanceLoadErrors((current) => ({
        ...current,
        goals: 'Shared goals could not be loaded. Please refresh and try again.',
      }));
    });
    const stopCheckIns = safeOnSnapshot(collection(db, 'teams', teamId, 'mentoringPairings', pairing.id, 'checkIns'), (snap) => {
      setCheckIns(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }))
        .sort((a, b) => (a.dueAt?.toMillis?.() || 0) - (b.dueAt?.toMillis?.() || 0)));
      setGuidanceLoadErrors((current) => ({ ...current, checkIns: '' }));
    }, (loadError) => {
      console.error('Could not load mentoring check-ins.', loadError);
      setGuidanceLoadErrors((current) => ({
        ...current,
        checkIns: 'Check-ins could not be loaded. Please refresh and try again.',
      }));
    });
    return () => {
      stopGoals();
      stopCheckIns();
    };
  }, [pairing?.id, pairing?.programmeMode, teamId]);

  useEffect(() => {
    if (isAdmin || !pairing?.id || checkIns.length === 0) {
      setResponsesByCheckIn({});
      return undefined;
    }
    const stops = checkIns.map((checkIn) => safeOnSnapshot(
      collection(db, 'teams', teamId, 'mentoringPairings', pairing.id, 'checkIns', checkIn.id, 'responses'),
      (snap) => setResponsesByCheckIn((current) => ({
        ...current,
        [checkIn.id]: snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })),
      })),
      () => {}
    ));
    return () => stops.forEach((stop) => stop());
  }, [checkIns, isAdmin, pairing?.id, teamId]);

  if (pairing?.programmeMode !== 'guided') return null;
  const sharedResponseFor = (checkIn) => {
    if (!checkIn) return null;
    const storedResponses = responsesByCheckIn[checkIn.id] || [];
    const sharedResponse = storedResponses.find((entry) => entry.id === 'shared');
    if (sharedResponse) return sharedResponse;
    if (storedResponses.length === 0) return null;
    const mergeLegacyField = (field) => {
      const populatedResponses = storedResponses.filter((entry) => entry[field]);
      const uniqueValues = Array.from(new Set(populatedResponses.map((entry) => String(entry[field]).trim())));
      if (uniqueValues.length <= 1) return uniqueValues[0] || '';
      return populatedResponses
        .map((entry) => {
          const name = entry.memberId === pairing.mentorId ? pairing.mentorName : pairing.menteeName;
          return `${name || 'Participant'}: ${entry[field]}`;
        })
        .join('\n\n');
    };
    return {
      id: 'legacy-shared',
      progress: mergeLegacyField('progress'),
      nextSteps: mergeLegacyField('nextSteps'),
      supportNeeded: mergeLegacyField('supportNeeded'),
    };
  };
  const currentCheckIn = checkIns.find((entry) => entry.status !== 'complete');
  const completedCheckIns = checkIns.filter((entry) => entry.status === 'complete');
  const editingCheckIn = checkIns.find((entry) => entry.id === editingCheckInId) || null;
  const currentMeeting = guidedMeeting(currentCheckIn);
  const currentSharedResponse = sharedResponseFor(currentCheckIn);
  const currentCheckInHasSavedNotes = Boolean(currentSharedResponse);
  const currentCheckInDueAtMs = currentCheckIn?.dueAt?.toMillis?.() || 0;
  const currentCheckInIsFutureFollowUp = Boolean(
    currentCheckIn
      && completedCheckIns.length > 0
      && currentCheckInDueAtMs > Date.now()
      && !openedEarlyCheckInIds.includes(currentCheckIn.id)
  );
  const eligiblePreviousCheckIns = currentCheckIn
    ? completedCheckIns.filter((entry) => Number(entry.sequence || 0) < Number(currentCheckIn.sequence || Infinity))
    : completedCheckIns;
  const previousCheckIn = eligiblePreviousCheckIns[eligiblePreviousCheckIns.length - 1] || null;
  const previousSharedResponse = sharedResponseFor(previousCheckIn);
  const journeySteps = [
    {
      label: 'Kick off',
      detail: 'Agree expectations, boundaries, and ways of working.',
      complete: completedCheckIns.some((entry) => entry.kind === 'kickoff'),
    },
    {
      label: 'Set goals',
      detail: 'Turn priorities into shared goals and first actions.',
      complete: goals.length > 0 && completedCheckIns.some((entry) => ['goals', 'progress', 'reflection'].includes(entry.kind)),
    },
    {
      label: 'Build momentum',
      detail: 'Review learning, obstacles, support, and next actions.',
      complete: completedCheckIns.some((entry) => entry.kind === 'progress'),
    },
    {
      label: 'Reflect and close',
      detail: 'Review outcomes and agree what happens next.',
      complete: completedCheckIns.some((entry) => entry.kind === 'reflection') || pairing.guidedStatus === 'ready_to_close',
    },
  ];

  const addGoal = async (event) => {
    event.preventDefault();
    if (!goalTitle.trim()) return;
    setBusy(true);
    setError('');
    try {
      await call('saveMentoringGoal', { teamId, pairingId: pairing.id, title: goalTitle });
      setGoalTitle('');
    } catch (err) {
      setError(err?.message || 'Could not save the goal.');
    } finally {
      setBusy(false);
    }
  };

  const submitCheckIn = async (event) => {
    event.preventDefault();
    const targetCheckIn = editingCheckIn || currentCheckIn;
    if (!targetCheckIn) return;
    setBusy(true);
    setError('');
    try {
      await call('submitMentoringCheckIn', {
        teamId,
        pairingId: pairing.id,
        checkInId: targetCheckIn.id,
        ...response,
      });
      setResponsesByCheckIn((current) => {
        const existing = current[targetCheckIn.id] || [];
        return {
          ...current,
          [targetCheckIn.id]: [
            ...existing.filter((entry) => entry.id !== 'shared'),
            {
              id: 'shared',
              updatedBy: currentUserId,
              progress: response.progress,
              nextSteps: response.nextSteps,
              supportNeeded: response.supportNeeded,
            },
          ],
        };
      });
      setEditingCheckInId('');
      setResponse({ ...EMPTY_CHECKIN_RESPONSE });
    } catch (err) {
      setError(err?.message || 'Could not submit the check-in.');
    } finally {
      setBusy(false);
    }
  };

  const editResponse = (checkIn, existingResponse) => {
    const adminNote = (checkIn.adminSharedNotes || []).find((entry) => entry.memberId === currentUserId)?.note || '';
    setResponse({
      progress: existingResponse?.progress || '',
      nextSteps: existingResponse?.nextSteps || '',
      supportNeeded: existingResponse?.supportNeeded || '',
      attentionRequested: false,
      adminSharedNote: adminNote,
    });
    setEditingCheckInId(checkIn.id);
  };

  const cancelResponseEdit = () => {
    setEditingCheckInId('');
    setResponse({ ...EMPTY_CHECKIN_RESPONSE });
  };

  const toggleAdminCheckIn = (checkInId) => {
    setExpandedAdminCheckInIds((current) => (
      current.includes(checkInId)
        ? current.filter((id) => id !== checkInId)
        : [...current, checkInId]
    ));
  };

  const completeGoal = async (goal) => {
    setBusy(true);
    setError('');
    try {
      await call('saveMentoringGoal', {
        teamId,
        pairingId: pairing.id,
        goalId: goal.id,
        title: goal.title,
        description: goal.description || '',
        status: goal.status === 'complete' ? 'active' : 'complete',
      });
    } catch (err) {
      setError(err?.message || 'Could not update the goal.');
    } finally {
      setBusy(false);
    }
  };

  const resolveAttention = async (checkIn) => {
    setBusy(true);
    setError('');
    try {
      await call('resolveMentoringAttentionFlag', {
        teamId,
        pairingId: pairing.id,
        checkInId: checkIn.id,
      });
    } catch (err) {
      setError(err?.message || 'Could not resolve the attention flag.');
    } finally {
      setBusy(false);
    }
  };

  const responseForm = (checkIn, editing = false) => {
    const meeting = guidedMeeting(checkIn);
    return (
      <form className="mentoring-form mentoring-checkin-form" onSubmit={submitCheckIn}>
        <div className="mentoring-checkin-form__heading">
          <div>
            <strong>{editing ? 'Edit shared meeting record' : meeting.title}</strong>
            <span>{editing ? `${meeting.title} · ${dateLabel(checkIn.dueAt)}` : `Due ${dateLabel(checkIn.dueAt)}`}</span>
          </div>
          {editing && <span className="mentoring-status">Shared record</span>}
        </div>
        <label>{meeting.progressLabel}<textarea value={response.progress} onChange={(event) => setResponse({ ...response, progress: event.target.value })} /></label>
        <label>What will each of you do before the next conversation?<textarea value={response.nextSteps} onChange={(event) => setResponse({ ...response, nextSteps: event.target.value })} /></label>
        <label>What support, challenge, or change is needed?<textarea value={response.supportNeeded} onChange={(event) => setResponse({ ...response, supportNeeded: event.target.value })} /></label>
        <label className="mentoring-checkbox"><input type="checkbox" checked={response.attentionRequested} onChange={(event) => setResponse({ ...response, attentionRequested: event.target.checked })} /> Ask a mentoring admin to follow up</label>
        <label>Optional note to mentoring admins<textarea value={response.adminSharedNote} onChange={(event) => setResponse({ ...response, adminSharedNote: event.target.value })} /></label>
        <p className="text-muted mentoring-privacy-note">This is one shared record that both mentoring participants can view and edit. Admins see only completion, attention requests, and the optional note above. Linked parents can view the shared record for managed child accounts.</p>
        <div className="mentoring-form__actions">
          {editing && <button type="button" className="btn btn--secondary" onClick={cancelResponseEdit} disabled={busy}>Cancel</button>}
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Saving…' : editing ? 'Save shared record' : 'Complete check-in'}</button>
        </div>
      </form>
    );
  };

  return (
    <div className="mentoring-guidance">
      {Object.values(guidanceLoadErrors).filter(Boolean).map((message) => (
        <p className="text-danger mentoring-guidance__load-error" role="alert" key={message}>{message}</p>
      ))}
      <section className="mentoring-journey">
        <header className="mentoring-journey__header">
          <div>
            <h4>Programme journey</h4>
            <p>
              {pairing.plannedEndAt
                ? `${pairing.programmeLengthWeeks || 24}-week programme · planned finish ${dateLabel(pairing.plannedEndAt)}`
                : 'A structured journey from kick-off through goals, progress, and reflection.'}
            </p>
          </div>
          <span className="mentoring-status">
            {journeySteps.filter((step) => step.complete).length} of {journeySteps.length} stages
          </span>
        </header>
        <ol className="mentoring-journey__steps">
          {journeySteps.map((step, index) => {
            const priorComplete = journeySteps.slice(0, index).every((entry) => entry.complete);
            const current = !step.complete && priorComplete;
            return (
              <li key={step.label} className={step.complete ? 'is-complete' : current ? 'is-current' : ''}>
                <span className="mentoring-journey__marker" aria-hidden="true">{step.complete ? '✓' : index + 1}</span>
                <div>
                  <strong>{step.label}</strong>
                  <span>{step.detail}</span>
                </div>
              </li>
            );
          })}
        </ol>
      </section>
      {currentCheckIn && (
        <section className="mentoring-meeting-guide">
          <span className="mentoring-meeting-guide__eyebrow">Next guided conversation · due {dateLabel(currentCheckIn.dueAt)}</span>
          <h4>{currentMeeting.title}</h4>
          <ol>
            {currentMeeting.agenda.map((item) => <li key={item}>{item}</li>)}
          </ol>
          {!isAdmin && previousCheckIn && previousSharedResponse && (
            <div className="mentoring-meeting-guide__previous">
              <div>
                <strong>Bring forward from last time</strong>
                <span>{guidedMeeting(previousCheckIn).title} · {dateLabel(previousCheckIn.dueAt)}</span>
              </div>
              <div className="mentoring-meeting-guide__previous-responses">
                <article>
                  <strong>Shared meeting record</strong>
                  {previousSharedResponse.progress && <p><b>Progress:</b> {previousSharedResponse.progress}</p>}
                  {previousSharedResponse.nextSteps && <p><b>Agreed next steps:</b> {previousSharedResponse.nextSteps}</p>}
                  {previousSharedResponse.supportNeeded && <p><b>Support needed:</b> {previousSharedResponse.supportNeeded}</p>}
                </article>
              </div>
            </div>
          )}
        </section>
      )}
      <section>
        <h4>Shared goals</h4>
        {goals.length ? (
          <ul className="mentoring-goals-list">{goals.map((goal) => (
            <li key={goal.id}>
              <div>
                <strong>{goal.title}</strong>
                <small>{statusLabel(goal.status)}</small>
              </div>
              {!isAdmin && canSubmit && (
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => completeGoal(goal)} disabled={busy}>
                  {goal.status === 'complete' ? 'Reopen' : 'Complete'}
                </button>
              )}
            </li>
          ))}</ul>
        ) : <p className="text-muted">No goals have been added yet.</p>}
        {!isAdmin && canSubmit && (
          <form className="mentoring-inline-form" onSubmit={addGoal}>
            <input aria-label="New shared goal" value={goalTitle} onChange={(event) => setGoalTitle(event.target.value)} placeholder="Add a shared goal" />
            <button type="submit" className="btn btn--primary btn--sm" disabled={busy || !goalTitle.trim()}>
              {busy ? 'Adding…' : 'Add goal'}
            </button>
          </form>
        )}
      </section>
      <section>
        <h4>Check-ins</h4>
        {isAdmin ? (
          <div className="mentoring-checkin-list">
            {checkIns.map((checkIn) => {
              const expanded = expandedAdminCheckInIds.includes(checkIn.id);
              const meeting = guidedMeeting(checkIn);
              const contributorNames = Array.from(new Set((checkIn.completedBy || []).map((memberId) => (
                memberId === pairing.mentorId
                  ? pairing.mentorName || 'Mentor'
                  : memberId === pairing.menteeId
                  ? pairing.menteeName || 'Mentee'
                  : ''
              )).filter(Boolean)));
              return (
                <article className={`mentoring-admin-checkin ${expanded ? 'is-expanded' : ''}`} key={checkIn.id}>
                  <div className="mentoring-admin-checkin__summary">
                    <div>
                      <strong>{meeting.title}</strong>
                      <span>
                        {checkIn.status === 'complete'
                          ? `Completed ${dateLabel(checkIn.completedAt || checkIn.dueAt)}`
                          : `Due ${dateLabel(checkIn.dueAt)}`}
                      </span>
                    </div>
                    <span className={`mentoring-status mentoring-status--${checkIn.status}`}>{statusLabel(checkIn.status)}</span>
                    <button
                      type="button"
                      className="btn btn--secondary btn--sm"
                      onClick={() => toggleAdminCheckIn(checkIn.id)}
                      aria-expanded={expanded}
                    >
                      {expanded ? 'Hide form' : 'View form'}
                    </button>
                  </div>
                  <div className="mentoring-admin-checkin__completion">
                    <strong>
                      {checkIn.status === 'complete'
                        ? `Shared check-in completed${contributorNames.length ? ` by ${contributorNames.join(' and ')}` : ''}`
                        : 'Shared check-in not completed yet'}
                    </strong>
                    {checkIn.attentionRequested && <span className="mentoring-flag">Attention requested</span>}
                  </div>
                  {expanded && (
                    <div className="mentoring-admin-checkin__details">
                      <div className="mentoring-admin-checkin__privacy">
                        <strong>What administrators can see</strong>
                        <p>The shared answers remain private to the mentoring pair and applicable linked parents. Administrators can see completion, attention flags, and notes deliberately shared with the programme team.</p>
                      </div>
                      <div className="mentoring-admin-checkin__questions">
                        <strong>Form prompts</strong>
                        <ol>
                          <li>{meeting.progressLabel}</li>
                          <li>What will each of you do before the next conversation?</li>
                          <li>What support, challenge, or change is needed?</li>
                          <li>Would you like a mentoring administrator to follow up?</li>
                          <li>Optional note to mentoring administrators</li>
                        </ol>
                      </div>
                      {checkIn.attentionRequested && (
                        <div className="mentoring-checkin-list__actions">
                          <button type="button" className="btn btn--secondary btn--sm" onClick={() => resolveAttention(checkIn)} disabled={busy}>Mark followed up</button>
                        </div>
                      )}
                      {(checkIn.adminSharedNotes || []).map((note, index) => {
                        const authorName = note.memberId === pairing.mentorId
                          ? pairing.mentorName || 'Mentor'
                          : note.memberId === pairing.menteeId
                          ? pairing.menteeName || 'Mentee'
                          : 'Participant';
                        return (
                          <div className="mentoring-checkin-list__admin-note" key={index}>
                            <strong>{authorName} shared with mentoring admins</strong>
                            <p>{note.note}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        ) : editingCheckIn ? (
          responseForm(editingCheckIn, true)
        ) : currentCheckIn && canSubmit ? (
          currentCheckInHasSavedNotes ? (
            <div className="mentoring-checkin-submitted">
              <div className="mentoring-checkin-submitted__heading">
                <div>
                  <strong>Shared check-in notes are saved</strong>
                  <span>{currentMeeting.title} · {dateLabel(currentCheckIn.dueAt)}</span>
                </div>
                <span className="mentoring-status">Ready to complete</span>
              </div>
              <p>This is one shared meeting record for the mentoring pair. Either participant can review, edit, and complete it.</p>
              <article className="mentoring-checkin-submitted__response">
                {currentSharedResponse.progress && <p><b>Progress:</b> {currentSharedResponse.progress}</p>}
                {currentSharedResponse.nextSteps && <p><b>Next steps:</b> {currentSharedResponse.nextSteps}</p>}
                {currentSharedResponse.supportNeeded && <p><b>Support needed:</b> {currentSharedResponse.supportNeeded}</p>}
              </article>
              <div className="mentoring-checkin-submitted__actions">
                <button type="button" className="btn btn--primary btn--sm" onClick={() => editResponse(currentCheckIn, currentSharedResponse)}>Review and complete</button>
              </div>
            </div>
          ) : currentCheckInIsFutureFollowUp ? (
            <div className="mentoring-checkin-up-to-date">
              <div>
                <strong>You’re up to date until {dateLabel(currentCheckIn.dueAt)}</strong>
                <p>The next check-in will open automatically when it is due. Your previous discussion and agreed next steps will be shown above.</p>
              </div>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => setOpenedEarlyCheckInIds((current) => [...current, currentCheckIn.id])}
              >
                Open check-in early
              </button>
            </div>
          ) : responseForm(currentCheckIn)
        ) : <p className="text-muted">{currentCheckIn ? `Next check-in: ${dateLabel(currentCheckIn.dueAt)}` : 'No check-in is currently due.'}</p>}
        {!isAdmin && completedCheckIns.map((checkIn) => {
          const sharedResponse = sharedResponseFor(checkIn);
          if (!sharedResponse) return null;
          return (
            <div className="mentoring-private-responses" key={`responses-${checkIn.id}`}>
              <div className="mentoring-private-responses__heading">
                <div>
                  <strong>{guidedMeeting(checkIn).title}</strong>
                  <span>Completed {dateLabel(checkIn.completedAt || checkIn.dueAt)}</span>
                </div>
                <span className="mentoring-status mentoring-status--complete">Complete</span>
              </div>
              <article>
                <div className="mentoring-private-responses__response-heading">
                  <strong>Shared meeting record</strong>
                  {canSubmit && (
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => editResponse(checkIn, sharedResponse)}>Edit shared record</button>
                  )}
                </div>
                {sharedResponse.progress && <p><b>Progress:</b> {sharedResponse.progress}</p>}
                {sharedResponse.nextSteps && <p><b>Next steps:</b> {sharedResponse.nextSteps}</p>}
                {sharedResponse.supportNeeded && <p><b>Support needed:</b> {sharedResponse.supportNeeded}</p>}
              </article>
            </div>
          );
        })}
      </section>
      {error && <p className="text-danger" role="alert">{error}</p>}
    </div>
  );
}

function MentoringSettingsModal({ teamId, settings, onClose }) {
  const [draft, setDraft] = useState(settings);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !busy) onClose?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [busy, onClose]);

  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        'modules.mentoring.allowMemberApplications': draft.allowMemberApplications !== false,
        'modules.mentoring.programmeMode': draft.programmeMode === 'guided' ? 'guided' : 'matching_only',
        'modules.mentoring.allowUnder18s': draft.allowUnder18s === true,
        'modules.mentoring.defaultCheckInCadenceDays': Number(draft.defaultCheckInCadenceDays || 28),
        'modules.mentoring.programmeLengthWeeks': Number(draft.programmeLengthWeeks || 24),
      });
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Could not save mentoring settings.');
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop mentoring-settings-backdrop" role="presentation" onMouseDown={() => !busy && onClose?.()}>
      <form
        className="modal-content mentoring-settings-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mentoring-settings-title"
        onSubmit={save}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mentoring-modal__header">
          <div>
            <h2 id="mentoring-settings-title">Configure mentoring</h2>
            <p>Choose how members join and how much structure the programme provides.</p>
          </div>
          <button type="button" className="mentoring-modal__close" onClick={onClose} disabled={busy} aria-label="Close mentoring settings">×</button>
        </header>

        <div className="mentoring-modal__body">
          <label className="mentoring-setting-toggle">
            <span>
              <strong>Member applications</strong>
              <small>Let members offer to mentor, request a mentor, or do both.</small>
            </span>
            <input
              type="checkbox"
              checked={draft.allowMemberApplications !== false}
              onChange={(event) => setDraft({ ...draft, allowMemberApplications: event.target.checked })}
            />
          </label>

          <label className="mentoring-setting-field">
            <span>
              <strong>Programme depth</strong>
              <small>Matching only keeps the programme lightweight. Guided adds goals and scheduled check-ins.</small>
            </span>
            <select value={draft.programmeMode || 'matching_only'} onChange={(event) => setDraft({ ...draft, programmeMode: event.target.value })}>
              <option value="matching_only">Matching only</option>
              <option value="guided">Guided programme</option>
            </select>
          </label>

          {draft.programmeMode === 'guided' && (
            <>
              <div className="mentoring-guided-explainer">
                <strong>What Guided includes</strong>
                <span>Kick-off agreement → shared goals → recurring progress conversations → final reflection</span>
                <small>Each stage includes a suggested agenda, structured reflection, actions, reminders, and an escalation route to programme admins.</small>
              </div>
              <label className="mentoring-setting-field">
                <span>
                  <strong>Programme length</strong>
                  <small>The final conversation becomes a structured reflection rather than another open-ended reminder.</small>
                </span>
                <select value={Number(draft.programmeLengthWeeks || 24)} onChange={(event) => setDraft({ ...draft, programmeLengthWeeks: Number(event.target.value) })}>
                  <option value={8}>8 weeks</option>
                  <option value={12}>12 weeks</option>
                  <option value={16}>16 weeks</option>
                  <option value={24}>24 weeks</option>
                  <option value={36}>36 weeks</option>
                  <option value={52}>52 weeks</option>
                </select>
              </label>
              <label className="mentoring-setting-field">
                <span>
                  <strong>Conversation rhythm</strong>
                  <small>The kick-off is due in the first week. This rhythm applies to later guided conversations and can be overridden per pairing.</small>
                </span>
                <select value={Number(draft.defaultCheckInCadenceDays || 28)} onChange={(event) => setDraft({ ...draft, defaultCheckInCadenceDays: Number(event.target.value) })}>
                  <option value={7}>Weekly</option>
                  <option value={14}>Fortnightly</option>
                  <option value={28}>Every four weeks</option>
                  <option value={30}>Monthly</option>
                  <option value={60}>Every two months</option>
                  <option value={90}>Quarterly</option>
                </select>
              </label>
            </>
          )}

          <label className="mentoring-setting-toggle">
            <span>
              <strong>Under-18 participation</strong>
              <small>Requires a linked family parent to confirm participation. Parents remain included in mentoring visibility.</small>
            </span>
            <input
              type="checkbox"
              checked={draft.allowUnder18s === true}
              onChange={(event) => setDraft({ ...draft, allowUnder18s: event.target.checked })}
            />
          </label>
        </div>

        {error && <p className="text-danger mentoring-modal__error" role="alert">{error}</p>}
        <footer className="mentoring-modal__actions">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? 'Saving…' : 'Save settings'}</button>
        </footer>
      </form>
    </div>
  );
}

function AdminWorkspace({
  teamId,
  currentUserId,
  participants,
  invitations,
  pairings,
  settings,
  members,
  onEditOwnApplication,
  initialTab = 'overview',
}) {
  const [tab, setTab] = useState(initialTab);
  const [suggestions, setSuggestions] = useState([]);
  const [unmatchedCount, setUnmatchedCount] = useState(0);
  const [capacityByMember, setCapacityByMember] = useState({});
  const [excludeByMember, setExcludeByMember] = useState({});
  const [manualMentor, setManualMentor] = useState('');
  const [manualMentee, setManualMentee] = useState('');
  const [cadenceByPair, setCadenceByPair] = useState({});
  const [manualCadence, setManualCadence] = useState(Number(settings.defaultCheckInCadenceDays || 28));
  const [closureNotes, setClosureNotes] = useState({});
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [checkInsByPairing, setCheckInsByPairing] = useState({});
  const [reviewingParticipant, setReviewingParticipant] = useState(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pairingSearch, setPairingSearch] = useState('');
  const [pairingStatusFilter, setPairingStatusFilter] = useState('all');
  const [pairingModeFilter, setPairingModeFilter] = useState('all');
  const [expandedPairingIds, setExpandedPairingIds] = useState([]);
  const activePairings = pairings.filter((entry) => entry.status === 'active');
  const activePairingIdsKey = activePairings.map((entry) => entry.id).sort().join('|');
  const approvedMentors = participants.filter((entry) => entry.status === 'approved' && entry.approvedRoles?.includes('mentor'));
  const approvedMentees = participants.filter((entry) => entry.status === 'approved' && entry.approvedRoles?.includes('mentee'));
  const usedCapacity = activePairings.reduce((map, pairing) => {
    map[pairing.mentorId] = (map[pairing.mentorId] || 0) + 1;
    return map;
  }, {});
  const availableCapacity = approvedMentors.reduce((total, mentor) => (
    total + Math.max(0, Math.min(mentor.mentorCapacityRequested || 1, mentor.mentorCapacityApproved || 1) - (usedCapacity[mentor.id] || 0))
  ), 0);
  const participantsById = new Map(participants.map((participant) => [participant.id, participant]));
  const activeMenteeIds = new Set(activePairings.map((pairing) => pairing.menteeId));
  const clubMembers = (members || []).map((member) => {
    const participant = participantsById.get(member.id);
    const safeName = member.name && member.name !== member.id
      ? member.name
      : participant?.displayName || 'Unnamed member';
    return {
      id: member.id,
      displayName: safeName,
      participant,
    };
  });
  const manualMentorOptions = clubMembers
    .filter((member) => {
      const participant = member.participant;
      if (!participant?.approvedRoles?.includes('mentor') || participant.status !== 'approved') return true;
      return Math.min(participant.mentorCapacityRequested || 1, participant.mentorCapacityApproved || 1)
        - (usedCapacity[member.id] || 0) > 0;
    })
    .map((member) => ({
      ...member,
      pickerNote: member.participant?.status === 'approved' && member.participant?.approvedRoles?.includes('mentor')
        ? 'Approved mentor'
        : 'Admin override · will be added as a mentor',
    }));
  const manualMenteeOptions = clubMembers
    .filter((member) => !activeMenteeIds.has(member.id))
    .map((member) => ({
      ...member,
      pickerNote: member.participant?.status === 'approved' && member.participant?.approvedRoles?.includes('mentee')
        ? 'Approved mentee'
        : 'Admin override · will be added as a mentee',
    }));
  useEffect(() => {
    const pairingIds = activePairingIdsKey.split('|').filter(Boolean);
    if (!pairingIds.length) {
      setCheckInsByPairing({});
      return undefined;
    }
    const stops = pairingIds.map((pairingId) => safeOnSnapshot(
      collection(db, 'teams', teamId, 'mentoringPairings', pairingId, 'checkIns'),
      (snap) => setCheckInsByPairing((current) => ({
        ...current,
        [pairingId]: snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })),
      })),
      () => {}
    ));
    return () => stops.forEach((stop) => stop());
  }, [activePairingIdsKey, teamId]);
  const allCheckIns = Object.values(checkInsByPairing).flat();
  const overdueCount = allCheckIns.filter((entry) => entry.status === 'overdue').length;
  const attentionCount = allCheckIns.filter((entry) => entry.attentionRequested === true).length;
  const invitationStatus = (invitation) => (
    ['sent', 'opened'].includes(invitation.status) && invitation.deadlineAt?.toMillis?.() < Date.now()
      ? 'expired'
      : invitation.status
  );

  const review = async (participant, status) => {
    if (status === 'rejected' && !window.confirm(`Reject ${participant.displayName || 'this mentoring applicant'}? They will need to apply again to re-enter the programme.`)) return;
    setBusy(participant.id);
    setError('');
    try {
      await call('reviewMentoringParticipant', {
        teamId,
        memberId: participant.id,
        status,
        approvedRoles: participant.requestedRoles,
        mentorCapacityApproved: Number(capacityByMember[participant.id] || participant.mentorCapacityApproved || participant.mentorCapacityRequested || 1),
      });
      return true;
    } catch (err) {
      setError(err?.message || 'Could not update the participant.');
      return false;
    } finally {
      setBusy('');
    }
  };

  const setExclusion = async (memberId, otherMemberId, blocked) => {
    if (!memberId || !otherMemberId) return;
    setBusy(`exclude:${memberId}`);
    setError('');
    try {
      await call('setMentoringExclusion', { teamId, memberId, otherMemberId, blocked });
      setExcludeByMember((current) => ({ ...current, [memberId]: '' }));
    } catch (err) {
      setError(err?.message || 'Could not update the matching exclusion.');
    } finally {
      setBusy('');
    }
  };

  const resendInvitation = async (invitation) => {
    setBusy(`invite:${invitation.id}`);
    setError('');
    try {
      await call('sendMentoringInvitation', {
        teamId,
        memberId: invitation.inviteeId,
        roles: invitation.invitedRoles,
        note: invitation.note || '',
        deadlineDays: 14,
      });
    } catch (err) {
      setError(err?.message || 'Could not resend the mentoring invitation.');
    } finally {
      setBusy('');
    }
  };

  const cancelInvitation = async (invitation) => {
    if (!window.confirm(`Cancel the mentoring invitation for ${invitation.inviteeName || 'this member'}?`)) return;
    setBusy(`invite:${invitation.id}`);
    setError('');
    try {
      await call('cancelMentoringInvitation', { teamId, memberId: invitation.inviteeId });
    } catch (err) {
      setError(err?.message || 'Could not cancel the mentoring invitation.');
    } finally {
      setBusy('');
    }
  };

  const runMatching = async () => {
    setBusy('matching');
    setError('');
    try {
      const result = await call('createMentoringSuggestions', { teamId });
      setSuggestions(result.suggestions || []);
      setUnmatchedCount(result.unmatchedMenteeIds?.length || 0);
      setTab('matching');
    } catch (err) {
      setError(err?.message || 'Could not generate suggestions.');
    } finally {
      setBusy('');
    }
  };

  const confirm = async ({ mentorId, menteeId, score = 0, reasons = [], warnings = [], cadenceDays }) => {
    setBusy(`${mentorId}:${menteeId}`);
    setError('');
    try {
      await call('confirmMentoringPairing', {
        teamId,
        mentorId,
        menteeId,
        score,
        reasons,
        warnings,
        cadenceDays,
        adminOverride: warnings.includes('Manually selected by an administrator'),
      });
      setSuggestions((current) => current.filter((entry) => entry.mentorId !== mentorId || entry.menteeId !== menteeId));
      setManualMentor('');
      setManualMentee('');
    } catch (err) {
      setError(err?.message || 'Could not confirm the pairing.');
    } finally {
      setBusy('');
    }
  };

  const endPairing = async (pairing) => {
    if (!window.confirm(`End the pairing between ${pairing.mentorName || 'the mentor'} and ${pairing.menteeName || 'the mentee'}?`)) return;
    setBusy(pairing.id);
    setError('');
    try {
      await call('endMentoringPairing', { teamId, pairingId: pairing.id, closureNote: pairing.closureNote || '' });
    } catch (err) {
      setError(err?.message || 'Could not end the pairing.');
    } finally {
      setBusy('');
    }
  };

  const nameFor = (id) => participants.find((entry) => entry.id === id)?.displayName
    || clubMembers.find((entry) => entry.id === id)?.displayName
    || 'Member';
  const pairingSearchNeedle = pairingSearch.trim().toLocaleLowerCase();
  const filteredPairings = pairings
    .filter((pairing) => (
      pairingStatusFilter === 'all' || pairing.status === pairingStatusFilter
    ))
    .filter((pairing) => (
      pairingModeFilter === 'all' || pairing.programmeMode === pairingModeFilter
    ))
    .filter((pairing) => {
      if (!pairingSearchNeedle) return true;
      return [
        pairing.mentorName || nameFor(pairing.mentorId),
        pairing.menteeName || nameFor(pairing.menteeId),
        statusLabel(pairing.status),
        pairing.programmeMode === 'guided' ? 'Guided programme' : 'Matching only',
      ].some((value) => String(value || '').toLocaleLowerCase().includes(pairingSearchNeedle));
    })
    .sort((left, right) => {
      if (left.status === 'active' && right.status !== 'active') return -1;
      if (right.status === 'active' && left.status !== 'active') return 1;
      const leftTime = left.updatedAt?.toMillis?.() || left.startedAt?.toMillis?.() || 0;
      const rightTime = right.updatedAt?.toMillis?.() || right.startedAt?.toMillis?.() || 0;
      return rightTime - leftTime;
    });
  const pairingFiltersActive = Boolean(pairingSearchNeedle)
    || pairingStatusFilter !== 'all'
    || pairingModeFilter !== 'all';
  const togglePairingDetails = (pairingId) => {
    setExpandedPairingIds((current) => (
      current.includes(pairingId)
        ? current.filter((id) => id !== pairingId)
        : [...current, pairingId]
    ));
  };
  const clearPairingFilters = () => {
    setPairingSearch('');
    setPairingStatusFilter('all');
    setPairingModeFilter('all');
  };
  const unpairedMenteeIds = new Set(approvedMentees.map((entry) => entry.id));
  activePairings.forEach((entry) => unpairedMenteeIds.delete(entry.menteeId));

  return (
    <div className="mentoring-workspace">
      <nav className="mentoring-tabs" aria-label="Mentoring administration">
        {['overview', 'participants', 'matching', 'pairings'].map((value) => (
          <button key={value} type="button" className={tab === value ? 'is-active' : ''} onClick={() => setTab(value)}>{statusLabel(value)}</button>
        ))}
      </nav>
      {error && <p className="text-danger" role="alert">{error}</p>}
      {tab === 'overview' && (
        <>
          <div className="mentoring-metrics">
            <Metric value={participants.filter((entry) => ['pending', 'pending_parent'].includes(entry.status)).length} label="Awaiting review" />
            <Metric value={activePairings.length} label="Active pairings" />
            <Metric value={availableCapacity} label="Available mentor places" />
            <Metric value={unpairedMenteeIds.size} label="Unmatched mentees" />
            <Metric value={overdueCount} label="Overdue check-ins" />
            <Metric value={attentionCount} label="Attention flags" />
          </div>
          <div className="mentoring-overview__actions">
            <button type="button" className="btn btn--primary btn--sm" onClick={runMatching} disabled={busy === 'matching'}>
              {busy === 'matching' ? 'Balancing…' : 'Generate balanced matches'}
            </button>
          </div>
        </>
      )}
      {tab === 'participants' && (
        <div className="mentoring-list">
          <div className="mentoring-participants-toolbar">
            <div>
              <strong>Programme participants</strong>
              <span>Review applications, manage eligibility, and maintain matching exclusions.</span>
            </div>
            <div className="mentoring-participants-toolbar__actions">
              <button type="button" className="btn btn--secondary btn--sm" onClick={onEditOwnApplication}>
                {participantsById.has(currentUserId) ? 'Edit my mentoring profile' : 'Offer myself as a mentor'}
              </button>
              <button type="button" className="btn btn--primary btn--sm" onClick={() => setInviteOpen(true)}>Invite member</button>
            </div>
          </div>
          {invitations.length > 0 && (
            <section className="mentoring-invitations-panel">
              <h4>Invitations</h4>
              <div className="mentoring-invitations-list">
                {invitations
                  .slice()
                  .sort((left, right) => String(left.inviteeName || '').localeCompare(String(right.inviteeName || '')))
                  .map((invitation) => {
                    const status = invitationStatus(invitation);
                    return (
                      <article key={invitation.id}>
                        <div>
                          <strong>{invitation.inviteeName || 'Member'}</strong>
                          <span>{invitation.invitedRoles?.map(roleLabel).join(' or ')} · {statusLabel(status)}</span>
                          <small>Respond by {dateLabel(invitation.deadlineAt)}</small>
                        </div>
                        <div className="mentoring-row__actions">
                          {['sent', 'opened', 'expired', 'declined'].includes(status) && (
                            <button type="button" className="btn btn--ghost btn--sm" onClick={() => resendInvitation(invitation)} disabled={busy === `invite:${invitation.id}`}>Resend</button>
                          )}
                          {['sent', 'opened'].includes(status) && (
                            <button type="button" className="btn btn--ghost btn--sm" onClick={() => cancelInvitation(invitation)} disabled={busy === `invite:${invitation.id}`}>Cancel</button>
                          )}
                        </div>
                      </article>
                    );
                  })}
              </div>
            </section>
          )}
          {participants.length === 0 && <p className="text-muted">No mentoring applications yet.</p>}
          {participants.map((participant) => (
            <article key={participant.id} className="mentoring-row">
              <div>
                <strong>{participant.displayName || 'Member'}</strong>
                <span>
                  {statusLabel(participant.status)} · {(participant.approvedRoles?.length
                    ? participant.approvedRoles
                    : participant.requestedRoles || []).map(roleLabel).join(' and ') || 'No role'}
                  {participant.adminAssignedRoles?.length ? ' · Admin assigned' : ''}
                </span>
                {participant.isUnder18 && <small>Parent confirmation: {participant.parentConfirmed ? 'received' : 'required'}</small>}
              </div>
              <div className="mentoring-row__actions">
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  onClick={() => setReviewingParticipant(participant)}
                >
                  {participant.status === 'pending' || participant.status === 'pending_parent' ? 'Review application' : 'View application'}
                </button>
              </div>
              <div className="mentoring-exclusions">
                <label>
                  Do not pair with
                  <select value={excludeByMember[participant.id] || ''} onChange={(event) => setExcludeByMember({ ...excludeByMember, [participant.id]: event.target.value })}>
                    <option value="">Choose participant</option>
                    {participants.filter((entry) => entry.id !== participant.id && !(participant.blockedMemberIds || []).includes(entry.id)).map((entry) => (
                      <option key={entry.id} value={entry.id}>{entry.displayName || 'Member'}</option>
                    ))}
                  </select>
                </label>
                <button type="button" className="btn btn--secondary btn--sm" onClick={() => setExclusion(participant.id, excludeByMember[participant.id], true)} disabled={!excludeByMember[participant.id] || Boolean(busy)}>Add exclusion</button>
                {(participant.blockedMemberIds || []).map((blockedId) => (
                  <button type="button" className="btn btn--ghost btn--sm" key={blockedId} onClick={() => setExclusion(participant.id, blockedId, false)} disabled={Boolean(busy)}>
                    Remove {participants.find((entry) => entry.id === blockedId)?.displayName || 'participant'} exclusion
                  </button>
                ))}
              </div>
            </article>
          ))}
          {reviewingParticipant && (
            <ParticipantReviewModal
              participant={reviewingParticipant}
              capacity={capacityByMember[reviewingParticipant.id]
                ?? reviewingParticipant.mentorCapacityApproved
                ?? reviewingParticipant.mentorCapacityRequested
                ?? 1}
              onCapacityChange={(value) => setCapacityByMember((current) => ({
                ...current,
                [reviewingParticipant.id]: value,
              }))}
              onReview={review}
              busy={busy === reviewingParticipant.id}
              onClose={() => setReviewingParticipant(null)}
            />
          )}
          {inviteOpen && (
            <MentoringInviteModal
              teamId={teamId}
              members={clubMembers}
              currentUserId={currentUserId}
              onClose={() => setInviteOpen(false)}
            />
          )}
        </div>
      )}
      {tab === 'matching' && (
        <>
          <details className="mentoring-matching-explainer">
            <summary>How balanced matching works</summary>
            <ol>
              <li>Only approved, eligible participants are considered. Existing pairings, capacity, exclusions, self-pairing, and youth safeguards are treated as hard limits.</li>
              <li>Mentees with the fewest possible mentors are allocated first so flexible mentees do not take their only viable place.</li>
              <li>Each mentor is expanded into their remaining places. Later places receive a small load penalty, encouraging the system to spread pairings where compatibility is otherwise similar.</li>
              <li>Compatibility rewards support needs, availability, meeting format, interests, and preferred rhythm. Missing answers are flagged instead of guessed.</li>
              <li>Suggestions are deterministic and explainable. Nothing is paired until an administrator reviews and confirms it.</li>
            </ol>
          </details>
          <div className="mentoring-list">
            {suggestions.map((suggestion) => (
              <article className="mentoring-row" key={`${suggestion.mentorId}:${suggestion.menteeId}`}>
                <div>
                  <strong>{nameFor(suggestion.mentorId)} → {nameFor(suggestion.menteeId)}</strong>
                  <span>{suggestion.score}% compatibility · load {suggestion.mentorLoadAfterMatch}/{suggestion.mentorCapacity}</span>
                  <small>{suggestion.reasons?.join(' · ') || 'No questionnaire overlap identified'}</small>
                  {suggestion.warnings?.map((warning) => <small className="mentoring-warning" key={warning}>{warning}</small>)}
                </div>
                <div className="mentoring-row__actions">
                  {settings.programmeMode === 'guided' && (
                    <select
                      aria-label="Check-in cadence"
                      value={cadenceByPair[`${suggestion.mentorId}:${suggestion.menteeId}`] || Number(settings.defaultCheckInCadenceDays || 28)}
                      onChange={(event) => setCadenceByPair({
                        ...cadenceByPair,
                        [`${suggestion.mentorId}:${suggestion.menteeId}`]: Number(event.target.value),
                      })}
                    >
                      <option value={7}>Weekly</option>
                      <option value={14}>Fortnightly</option>
                      <option value={28}>Every four weeks</option>
                      <option value={30}>Monthly</option>
                      <option value={60}>Every two months</option>
                      <option value={90}>Quarterly</option>
                    </select>
                  )}
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => confirm({
                    ...suggestion,
                    cadenceDays: cadenceByPair[`${suggestion.mentorId}:${suggestion.menteeId}`] || Number(settings.defaultCheckInCadenceDays || 28),
                  })} disabled={Boolean(busy)}>Confirm</button>
                </div>
              </article>
            ))}
            {suggestions.length === 0 && <p className="text-muted">Generate suggestions or create a manual pairing below.</p>}
            {unmatchedCount > 0 && <p className="mentoring-warning">{unmatchedCount} mentee{unmatchedCount === 1 ? '' : 's'} could not be matched within current capacity and constraints.</p>}
          </div>
          <div className="mentoring-manual">
            <h4>Manual pairing</h4>
            <MentoringMemberPicker
              label="Mentor"
              members={manualMentorOptions.filter((entry) => entry.id !== manualMentee)}
              value={manualMentor}
              onChange={setManualMentor}
              placeholder="Choose mentor"
              emptyMessage="No eligible club members match that search."
            />
            <MentoringMemberPicker
              label="Mentee"
              members={manualMenteeOptions.filter((entry) => entry.id !== manualMentor)}
              value={manualMentee}
              onChange={setManualMentee}
              placeholder="Choose mentee"
              emptyMessage="No unmatched club members match that search."
            />
            {settings.programmeMode === 'guided' && (
              <select aria-label="Manual pairing check-in cadence" value={manualCadence} onChange={(event) => setManualCadence(Number(event.target.value))}>
                <option value={7}>Weekly check-ins</option>
                <option value={14}>Fortnightly check-ins</option>
                <option value={28}>Every four weeks</option>
                <option value={30}>Monthly check-ins</option>
                <option value={60}>Every two months</option>
                <option value={90}>Quarterly check-ins</option>
              </select>
            )}
            <button type="button" className="btn btn--primary btn--sm" onClick={() => confirm({ mentorId: manualMentor, menteeId: manualMentee, cadenceDays: manualCadence, warnings: ['Manually selected by an administrator'] })} disabled={!manualMentor || !manualMentee || Boolean(busy)}>Confirm manual pairing</button>
          </div>
        </>
      )}
      {tab === 'pairings' && (
        <div className="mentoring-pairings-workspace">
          <div className="mentoring-pairing-filters">
            <label className="mentoring-pairing-filters__search">
              <span>Search pairings</span>
              <input
                type="search"
                className="input"
                value={pairingSearch}
                onChange={(event) => setPairingSearch(event.target.value)}
                placeholder="Search mentor or mentee…"
              />
            </label>
            <label>
              <span>Status</span>
              <select value={pairingStatusFilter} onChange={(event) => setPairingStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="ended">Ended</option>
              </select>
            </label>
            <label>
              <span>Programme</span>
              <select value={pairingModeFilter} onChange={(event) => setPairingModeFilter(event.target.value)}>
                <option value="all">All programmes</option>
                <option value="guided">Guided</option>
                <option value="matching_only">Matching only</option>
              </select>
            </label>
            <div className="mentoring-pairing-filters__summary" aria-live="polite">
              <span>{filteredPairings.length} of {pairings.length} pairing{pairings.length === 1 ? '' : 's'}</span>
              {pairingFiltersActive && (
                <button type="button" className="btn btn--ghost btn--sm" onClick={clearPairingFilters}>Clear filters</button>
              )}
            </div>
          </div>
          {pairings.length === 0 ? (
            <p className="text-muted">No pairings yet.</p>
          ) : filteredPairings.length === 0 ? (
            <div className="mentoring-pairings-empty">
              <strong>No pairings match these filters.</strong>
              <button type="button" className="btn btn--secondary btn--sm" onClick={clearPairingFilters}>Clear filters</button>
            </div>
          ) : (
            <div className="mentoring-pairing-list">
              <div className="mentoring-pairing-list__header" aria-hidden="true">
                <span>Mentor</span>
                <span>Mentee</span>
                <span>Programme</span>
                <span>Status</span>
                <span>Date</span>
                <span />
              </div>
              {filteredPairings.map((pairing) => {
                const expanded = expandedPairingIds.includes(pairing.id);
                const isParticipant = [pairing.mentorId, pairing.menteeId].includes(currentUserId);
                return (
                  <article className={`mentoring-pairing-row ${expanded ? 'is-expanded' : ''}`} key={pairing.id}>
                    <div className="mentoring-pairing-row__summary">
                      <div data-label="Mentor"><strong>{pairing.mentorName || nameFor(pairing.mentorId)}</strong></div>
                      <div data-label="Mentee"><strong>{pairing.menteeName || nameFor(pairing.menteeId)}</strong></div>
                      <div data-label="Programme">{pairing.programmeMode === 'guided' ? 'Guided' : 'Matching only'}</div>
                      <div data-label="Status">
                        <span className={`mentoring-status mentoring-status--${pairing.status || 'unknown'}`}>{statusLabel(pairing.status)}</span>
                      </div>
                      <div data-label={pairing.status === 'ended' ? 'Ended' : 'Started'}>
                        {dateLabel(pairing.status === 'ended' ? pairing.endedAt : pairing.startedAt)}
                      </div>
                      <button
                        type="button"
                        className="btn btn--secondary btn--sm mentoring-pairing-row__toggle"
                        onClick={() => togglePairingDetails(pairing.id)}
                        aria-expanded={expanded}
                        aria-controls={`mentoring-pairing-details-${pairing.id}`}
                      >
                        {expanded ? 'Hide details' : 'View details'}
                      </button>
                    </div>
                    {expanded && (
                      <div className="mentoring-pairing-row__details" id={`mentoring-pairing-details-${pairing.id}`}>
                        {pairing.status === 'active' && (
                          <>
                            {isParticipant && (
                              <div className="mentoring-pairing__actions">
                                <a className="btn btn--secondary btn--sm" href={`/messages/${pairing.threadId}`}>Open conversation</a>
                              </div>
                            )}
                            <div className="mentoring-pairing__closure">
                              <label>
                                Closure note
                                <textarea value={closureNotes[pairing.id] || ''} onChange={(event) => setClosureNotes({ ...closureNotes, [pairing.id]: event.target.value })} placeholder="Optional outcome or reason" />
                              </label>
                              <button type="button" className="btn btn--danger btn--sm" onClick={() => endPairing({ ...pairing, closureNote: closureNotes[pairing.id] || '' })} disabled={busy === pairing.id}>
                                {busy === pairing.id ? 'Ending…' : 'End pairing'}
                              </button>
                            </div>
                          </>
                        )}
                        {pairing.status === 'ended' && pairing.closureNote && (
                          <div className="mentoring-pairing-row__closure-note">
                            <strong>Closure note</strong>
                            <p>{pairing.closureNote}</p>
                          </div>
                        )}
                        <PairingGuidance teamId={teamId} pairing={pairing} currentUserId={currentUserId} isAdmin />
                        {pairing.programmeMode !== 'guided' && (
                          <p className="text-muted mentoring-pairing-row__mode-note">This pairing uses matching-only mode, so there is no guided journey or scheduled check-in programme.</p>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MentoringAdminSummary({ participants, pairings, onOpen }) {
  const activePairings = pairings.filter((entry) => entry.status === 'active');
  const usedCapacity = activePairings.reduce((map, pairing) => {
    map[pairing.mentorId] = (map[pairing.mentorId] || 0) + 1;
    return map;
  }, {});
  const approvedMentors = participants.filter((entry) => (
    entry.status === 'approved' && entry.approvedRoles?.includes('mentor')
  ));
  const approvedMentees = participants.filter((entry) => (
    entry.status === 'approved' && entry.approvedRoles?.includes('mentee')
  ));
  const availableCapacity = approvedMentors.reduce((total, mentor) => (
    total + Math.max(
      0,
      Math.min(mentor.mentorCapacityRequested || 1, mentor.mentorCapacityApproved || 1)
        - (usedCapacity[mentor.id] || 0)
    )
  ), 0);
  const pairedMenteeIds = new Set(activePairings.map((pairing) => pairing.menteeId));
  const unmatchedMentees = approvedMentees.filter((mentee) => !pairedMenteeIds.has(mentee.id)).length;

  return (
    <div className="mentoring-admin-summary">
      <div className="mentoring-metrics">
        <Metric value={participants.filter((entry) => ['pending', 'pending_parent'].includes(entry.status)).length} label="Awaiting review" />
        <Metric value={activePairings.length} label="Active pairings" />
        <Metric value={availableCapacity} label="Mentor places" />
        <Metric value={unmatchedMentees} label="Unmatched mentees" />
      </div>
      <div className="mentoring-admin-summary__actions">
        <button type="button" className="btn btn--primary btn--sm" onClick={() => onOpen('overview')}>Manage programme</button>
        {participants.some((entry) => ['pending', 'pending_parent'].includes(entry.status)) && (
          <button type="button" className="btn btn--secondary btn--sm" onClick={() => onOpen('participants')}>Review applications</button>
        )}
      </div>
    </div>
  );
}

function MentoringPairingModal({ teamId, userId, pairing, onClose }) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);
  const isMentor = pairing.mentorId === userId;
  const partnerName = isMentor ? pairing.menteeName : pairing.mentorName;

  return (
    <MentoringModalPortal>
      <div className="modal-backdrop mentoring-pairing-backdrop" role="presentation" onMouseDown={onClose}>
        <div
          className="modal-content mentoring-pairing-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mentoring-pairing-title"
          onMouseDown={(event) => event.stopPropagation()}
        >
          <header className="mentoring-modal__header">
            <div>
              <h2 id="mentoring-pairing-title">Mentoring with {partnerName || 'your mentoring partner'}</h2>
              <p>You are participating as the {isMentor ? 'mentor' : 'mentee'}.</p>
            </div>
            <button type="button" className="mentoring-modal__close" onClick={onClose} aria-label="Close mentoring pairing">×</button>
          </header>
          <div className="mentoring-pairing-modal__body">
            <div className="mentoring-pairing-modal__actions">
              <a className="btn btn--primary btn--sm" href={`/messages/${pairing.threadId}`}>Open conversation</a>
            </div>
            <PairingGuidance teamId={teamId} pairing={pairing} currentUserId={userId} />
          </div>
        </div>
      </div>
    </MentoringModalPortal>
  );
}

function MentoringAdminWorkspaceModal({
  teamId,
  currentUserId,
  participants,
  invitations,
  pairings,
  settings,
  members,
  initialTab,
  onEditOwnApplication,
  onClose,
}) {
  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [onClose]);

  return (
    <MentoringModalPortal>
    <div className="modal-backdrop mentoring-admin-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="modal-content mentoring-admin-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mentoring-admin-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="mentoring-modal__header">
          <div>
            <h2 id="mentoring-admin-title">Manage mentoring</h2>
            <p>Review participants, create balanced matches, and manage active pairings.</p>
          </div>
          <button type="button" className="mentoring-modal__close" onClick={onClose} aria-label="Close mentoring management">×</button>
        </header>
        <div className="mentoring-admin-modal__body">
          <AdminWorkspace
            teamId={teamId}
            currentUserId={currentUserId}
            participants={participants}
            invitations={invitations}
            pairings={pairings}
            settings={settings}
            members={members}
            onEditOwnApplication={onEditOwnApplication}
            initialTab={initialTab}
          />
        </div>
      </div>
    </div>
    </MentoringModalPortal>
  );
}

export default function MentoringCard({
  teamId,
  userId,
  isAdmin,
  settings = {},
  members = [],
  invitationRequested = false,
  onInvitationHandled,
}) {
  const [participants, setParticipants] = useState([]);
  const [pairings, setPairings] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [applicationOpen, setApplicationOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [adminWorkspaceTab, setAdminWorkspaceTab] = useState(null);
  const [selectedPairing, setSelectedPairing] = useState(null);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!teamId || !userId) return undefined;
    if (isAdmin) {
      const stopParticipants = safeOnSnapshot(collection(db, 'teams', teamId, 'mentoringParticipants'), (snap) => {
        setParticipants(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      }, (err) => setError(err?.message || 'Could not load mentoring participants.'));
      const stopPairings = safeOnSnapshot(collection(db, 'teams', teamId, 'mentoringPairings'), (snap) => {
        setPairings(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      }, (err) => setError(err?.message || 'Could not load mentoring pairings.'));
      const stopInvitations = safeOnSnapshot(collection(db, 'teams', teamId, 'mentoringInvitations'), (snap) => {
        setInvitations(snap.docs.map((entry) => ({ id: entry.id, ...entry.data() })));
      }, (err) => setError(err?.message || 'Could not load mentoring invitations.'));
      return () => {
        stopParticipants();
        stopPairings();
        stopInvitations();
      };
    }
    const handleMemberLoadError = (message) => (loadError) => {
      console.error(message, loadError);
      setError(message);
    };
    const stopParticipant = safeOnSnapshot(doc(db, 'teams', teamId, 'mentoringParticipants', userId), (snap) => {
      setParticipants(snap.exists() ? [{ id: snap.id, ...snap.data() }] : []);
    }, handleMemberLoadError('We could not load your mentoring application. Please refresh and try again.'));
    const pairingsByRole = { mentor: [], mentee: [] };
    const publishPairings = () => {
      const uniquePairings = new Map(
        [...pairingsByRole.mentor, ...pairingsByRole.mentee].map((pairing) => [pairing.id, pairing])
      );
      setPairings(Array.from(uniquePairings.values()));
    };
    const stopMentorPairings = safeOnSnapshot(
      query(collection(db, 'teams', teamId, 'mentoringPairings'), where('mentorId', '==', userId)),
      (snap) => {
        pairingsByRole.mentor = snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
        publishPairings();
      },
      handleMemberLoadError('We could not load your mentoring pairings. Please refresh and try again.')
    );
    const stopMenteePairings = safeOnSnapshot(
      query(collection(db, 'teams', teamId, 'mentoringPairings'), where('menteeId', '==', userId)),
      (snap) => {
        pairingsByRole.mentee = snap.docs.map((entry) => ({ id: entry.id, ...entry.data() }));
        publishPairings();
      },
      handleMemberLoadError('We could not load your mentoring pairing. Please refresh and try again.')
    );
    const stopInvitation = safeOnSnapshot(
      doc(db, 'teams', teamId, 'mentoringInvitations', userId),
      (snap) => setInvitations(snap.exists() ? [{ id: snap.id, ...snap.data() }] : []),
      handleMemberLoadError('We could not load your mentoring invitation. Please refresh and try again.')
    );
    return () => {
      stopParticipant();
      stopMentorPairings();
      stopMenteePairings();
      stopInvitation();
    };
  }, [isAdmin, teamId, userId]);

  const myApplication = participants.find((entry) => entry.id === userId) || null;
  const myInvitation = invitations.find((entry) => entry.id === userId) || null;
  const liveInvitation = myInvitation && ['sent', 'opened'].includes(myInvitation.status)
    && (!myInvitation.deadlineAt?.toMillis?.() || myInvitation.deadlineAt.toMillis() >= Date.now())
    ? myInvitation
    : null;
  const activePairings = useMemo(() => pairings.filter((entry) => entry.status === 'active'), [pairings]);
  const withdraw = async () => {
    if (!window.confirm('Withdraw your mentoring application? You will need to apply again to re-enter the programme.')) return;
    setWithdrawBusy(true);
    setError('');
    try {
      await call('withdrawMentoringApplication', { teamId });
    } catch (err) {
      setError(err?.message || 'Could not withdraw the application.');
    } finally {
      setWithdrawBusy(false);
    }
  };

  useEffect(() => {
    if (!invitationRequested || !liveInvitation) return;
    setApplicationOpen(true);
    if (liveInvitation.status === 'sent') {
      call('updateMentoringInvitation', { teamId, action: 'opened' }).catch(() => {});
    }
    onInvitationHandled?.();
  }, [invitationRequested, liveInvitation, onInvitationHandled, teamId]);

  const declineInvitation = async () => {
    if (!window.confirm('Decline this mentoring invitation?')) return;
    try {
      await call('updateMentoringInvitation', { teamId, action: 'declined' });
      setApplicationOpen(false);
    } catch (err) {
      setError(err?.message || 'Could not decline the mentoring invitation.');
    }
  };

  return (
    <div className="team-card mentoring-card">
      <div className="card-header-with-icon">
        <span className="card-title">Mentoring</span>
        <div className="mentoring-card__header-actions">
          {isAdmin && (
            <button type="button" className="btn btn--secondary btn--xs" onClick={() => setSettingsOpen(true)}>
              Configure
            </button>
          )}
          {!isAdmin && (activePairings.length > 0 || myApplication) && (
            <span className="mentoring-status">
              {activePairings.length > 0 ? 'Active' : statusLabel(myApplication.status)}
            </span>
          )}
        </div>
      </div>
      <div className="card-body">
        <p className="mentoring-card__intro">Support development through thoughtful, balanced mentoring relationships.</p>
        {error && <p className="text-danger" role="alert">{error}</p>}
        {isAdmin ? (
          <MentoringAdminSummary
            participants={participants}
            pairings={pairings}
            onOpen={setAdminWorkspaceTab}
          />
        ) : (
          <>
            {activePairings.map((pairing) => {
              const isMentor = pairing.mentorId === userId;
              const partnerName = isMentor ? pairing.menteeName : pairing.mentorName;
              return (
                <article className="mentoring-pairing-summary" key={pairing.id}>
                  <div>
                    <span>Your {isMentor ? 'mentee' : 'mentor'}</span>
                    <strong>{partnerName || 'Mentoring partner'}</strong>
                    <small>{pairing.programmeMode === 'guided' ? 'Guided programme' : 'Matching only'}</small>
                  </div>
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => setSelectedPairing(pairing)}>Open mentoring</button>
                </article>
              );
            })}
            {activePairings.length === 0 && (
              <div className="mentoring-member-empty">
                <strong>{liveInvitation ? 'You’ve been invited to mentoring' : myApplication ? 'Your application is with the programme team' : 'Build a useful mentoring relationship'}</strong>
                <p className="text-muted">
                  {liveInvitation
                    ? `${liveInvitation.sentByName || 'A programme administrator'} has asked you to complete a mentoring profile as ${liveInvitation.invitedRoles?.map(roleLabel).join(' or ').toLowerCase()}.`
                    : myApplication
                    ? `Current status: ${statusLabel(myApplication.status)}. We’ll show your pairing here once it is confirmed.`
                    : 'Offer your experience as a mentor, ask for support as a mentee, or do both.'}
                </p>
              </div>
            )}
            {activePairings.length === 0 && (settings.allowMemberApplications !== false || liveInvitation) && (
              <div className="mentoring-member-actions">
                <button type="button" className="btn btn--primary btn--sm" onClick={() => setApplicationOpen(true)}>
                  {liveInvitation ? 'Respond to invitation' : myApplication ? 'Update my application' : 'Apply for mentoring'}
                </button>
                {myApplication && activePairings.length === 0 && myApplication.status !== 'withdrawn' && (
                  <button type="button" className="btn btn--ghost btn--sm" onClick={withdraw} disabled={withdrawBusy}>
                    {withdrawBusy ? 'Withdrawing…' : 'Withdraw application'}
                  </button>
                )}
              </div>
            )}
            {settings.allowMemberApplications === false && !myApplication && !liveInvitation && <p className="text-muted">Member applications are currently closed.</p>}
          </>
        )}
      </div>
      {isAdmin && settingsOpen && (
        <MentoringSettingsModal teamId={teamId} settings={settings} onClose={() => setSettingsOpen(false)} />
      )}
      {isAdmin && adminWorkspaceTab && (
        <MentoringAdminWorkspaceModal
          teamId={teamId}
          currentUserId={userId}
          participants={participants}
          invitations={invitations}
          pairings={pairings}
          settings={settings}
          members={members}
          initialTab={adminWorkspaceTab}
          onEditOwnApplication={() => setApplicationOpen(true)}
          onClose={() => setAdminWorkspaceTab(null)}
        />
      )}
      {selectedPairing && (
        <MentoringPairingModal
          teamId={teamId}
          userId={userId}
          pairing={selectedPairing}
          onClose={() => setSelectedPairing(null)}
        />
      )}
      {applicationOpen && (
        <MentoringApplicationModal
          teamId={teamId}
          existing={myApplication}
          invitation={liveInvitation}
          onDecline={declineInvitation}
          onClose={() => setApplicationOpen(false)}
        />
      )}
    </div>
  );
}
